/**
 * הגדרת תפקידים — the customer DEFINING their own roles, not merely choosing from five.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * OA-0001 §3.2 WAS HALF TRUE UNTIL THIS SCREEN EXISTED
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 *   > *"GL-POINT supplies the authorization system; the customer OPERATES it."*
 *
 * An office could ASSIGN the five seeded roles and could not DEFINE a sixth. An office whose shape
 * did not match those five — a bookkeeper who may see identifiers but not create companies, a
 * part-time person with billing read and nothing else — had no move at all except to over-grant, and
 * over-granting is what every one of these rules exists to prevent.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS NOT A PANEL ON THE ASSIGNMENT SCREEN
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `TenantRolesScreen` must offer NO capability control. OA-0001 §3.7: *"they do not collapse into one
 * admin flag"*, and a control that granted every capability at once would be that flag wearing a
 * Hebrew label. `adminScreens.test.ts` counts the controls on that screen so it cannot appear.
 *
 * This screen is nothing BUT a capability list — because that is what a role IS. Putting the two on
 * one page would leave a picker that grants every capability at once one scroll below a picker that
 * grants a role, and the count test that guards the first would have to be relaxed to allow it.
 * Splitting them keeps the guard exact.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE CEILING APPLIES HERE MORE SHARPLY THAN IT DOES TO ASSIGNMENT
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `tenant-lifecycle.ts` names the reason: *a bounded administrator does not need to assign anything —
 * they edit a role they already hold.* Authoring is the SHORTER ESCALATION PATH, so the same
 * delegation ceiling that bounds assignment bounds this, and the refusal carries the same `excess`
 * list rendered by the same component. Nothing on this screen evaluates a ceiling; it states the rule
 * and renders whatever the server decided.
 *
 * ── EDITING REPLACES THE SET, AND THE FORM SAYS SO ─────────────────────────────────────────────
 *
 * The update route replaces a role's capability list. So choosing a role LOADS ITS CURRENT SET — a
 * form that started empty would turn "add one capability" into "remove the other fifteen" on the most
 * sensitive write an office has.
 *
 * ── AND AN EMPTY ROLE IS VALID ─────────────────────────────────────────────────────────────────
 *
 * A role that confers nothing is how an office models a job title before deciding what it may do. It
 * is also the only role a principal with an empty ceiling can author, which is the correct floor
 * rather than an edge case, and the screen neither blocks it nor warns about it.
 *
 * ── NO ROLE HERE GRANTS A COMPANY ──────────────────────────────────────────────────────────────
 *
 * D5 / CG-1, restated because this is where somebody composing a role will look for it: there is no
 * company on this form and no capability in the catalogue that confers one. Company access is a
 * separate table, a separate capability and a separate screen.
 */

import { InfoHint } from '../components/InfoHint';
import { useState } from 'react';
import { useLocale } from '../i18n/LocaleProvider';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { RoleRefusalNotice } from '../components/RoleRefusalNotice';
import { StateBlock } from '../components/StateBlock';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { RoleAssignmentRefusalReason } from '../api/contract';
import { ROLE_ASSIGNMENT_REFUSALS } from '../api/contract';
import {
  capabilityAreas,
  capabilityDescriptionHe,
  describedCapabilityCodes,
  roleLabel,
} from '../api/adminVocabulary';
import { REFUSED_WITHOUT_DETAIL_HE } from '../api/roleAssignmentText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureRoles, fixturesEnabled } from '../api/fixtures';
import { routes } from '../routes';


/** `''` ⇒ composing a NEW role. Otherwise the code of the role being edited. */
const NEW_ROLE = '';

type Outcome =
  | { readonly kind: 'saved'; readonly created: boolean; readonly capabilityCount: number }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function knownRefusal(reason: string): RoleAssignmentRefusalReason | null {
  const known: readonly string[] = ROLE_ASSIGNMENT_REFUSALS;
  return known.includes(reason) ? (reason as RoleAssignmentRefusalReason) : null;
}

export function TenantRoleAuthoringScreen(): JSX.Element {
  const { t } = useLocale();
  const ctx = useWorkContext();
  const [editing, setEditing] = useState<string>(NEW_ROLE);
  const [roleCode, setRoleCode] = useState('');
  const [displayNameHe, setDisplayNameHe] = useState('');
  const [capabilities, setCapabilities] = useState<readonly string[]>([]);
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const roles = fixturesEnabled() ? fixtureRoles : [];

  if (ctx.surface !== 'TENANT') {
    return <SurfaceNotice titleHe={t('settings.e.roleAuthoring')} requires="TENANT" actual={ctx.surface} />;
  }

  /** The catalogue this build has Hebrew for. A code with no Hebrew is never offered as a control. */
  const catalogue = describedCapabilityCodes();

  function choose(code: string): void {
    setEditing(code);
    setOutcome(null);
    if (code === NEW_ROLE) {
      setRoleCode('');
      setDisplayNameHe('');
      setCapabilities([]);
      return;
    }
    const role = roles.find((r) => r.roleCode === code);
    if (role === undefined) return;
    setRoleCode(role.roleCode);
    // THROUGH THE ACCESSOR HERE TOO, and that is deliberate rather than an oversight: the
    // list above shows the corrected wording, so a field that seeded itself from the raw value
    // would contradict the row the person just picked. For a role a tenant named themselves
    // the accessor returns the value untouched, so nobody's own words are edited; for a
    // seeded one, saving persists the correction the Owner asked for -- by their press, not
    // by a migration nobody ran.
    setDisplayNameHe(roleLabel(role));
    // THE CURRENT SET, because the route replaces it. See the header.
    setCapabilities(role.capabilities);
  }

  function toggle(code: string): void {
    setCapabilities((current) =>
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code],
    );
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSending(true);
    setOutcome(null);
    const isNew = editing === NEW_ROLE;
    void (async () => {
      try {
        if (isNew) {
          await callOperation('tenant.role.create', {
            body: { roleCode, displayNameHe, capabilities },
          });
        } else {
          // The role code comes from the ADDRESS the request is made on and is NOT re-read from the
          // body: a body that could rename the role being edited is a body that can edit a different
          // role than the URL names, and the audit row records the URL.
          await callOperation('tenant.role.update', {
            params: { role_code: editing },
            body: { displayNameHe, capabilities },
          });
        }
        setOutcome({ kind: 'saved', created: isNew, capabilityCount: capabilities.length });
      } catch (err: unknown) {
        setOutcome({ kind: 'failed', failure: classifyActionError(err) });
      } finally {
        setSending(false);
      }
    })();
  }

  return (
    <AppShell
      /* Every string here goes through `t()`; `translatedScreens.test.ts` fails the build
         if one Hebrew literal survives, which is what makes this a claim and not a note. */
      translated
      titleHe={t('settings.e.roleAuthoring')}
      /* BACK TO THE SETTINGS LANDING: the two role screens are TOPICS of one category now, and the
         strip above moves between them. 2026-10-28. */
      backToParent={{ labelHe: t('settings.title'), href: routes.tenantAdmin }}
    >
      {/*
        THE NOTICE AT THE TOP IS GONE. **Owner, 2026-10-28:** *"אני לא רוצה לראות הסברים בתוך
        המערכת. המערכת צריכה להיות נקיה."*

        It carried a definition of a role and the fact that a role with no permissions is valid —
        two sentences that stood on every visit and that nobody acts on. The ONE consequence that
        is genuinely a consequence, and that somebody really does skip, now sits beside the control
        that causes it and only when it applies: see the ⓘ on the permission list below.
      */}

      <form className="form" onSubmit={submit}>
        <fieldset className="field field--choice">
          <legend className="field__label">{t('authoring.whatYouDefine')}</legend>
          <label className="choice">
            <input
              type="radio"
              name="authoringTarget"
              value={NEW_ROLE}
              checked={editing === NEW_ROLE}
              onChange={() => choose(NEW_ROLE)}
            />
            <span>
              {t('authoring.newRole')}
              <span className="choice__hint">{t('authoring.addsToList')}</span>
            </span>
          </label>
          {roles.map((role) => (
            <label key={role.roleCode} className="choice">
              <input
                type="radio"
                name="authoringTarget"
                value={role.roleCode}
                checked={editing === role.roleCode}
                onChange={() => choose(role.roleCode)}
              />
              <span>
                {roleLabel(role)}
                <span className="choice__hint">
                  {`${t('roles.grantsCount')}: ${String(role.capabilities.length)}`}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {editing === NEW_ROLE ? (
          <label className="field">
            <span className="field__label">{t('authoring.roleCode')}</span>
            <input
              className="field__input"
              name="roleCode"
              dir="ltr"
              value={roleCode}
              onChange={(e) => setRoleCode(e.currentTarget.value)}
            />
            <span className="field__hint">
              {t('authoring.roleCodeHint')}
            </span>
          </label>
        ) : (
          <p className="form__note">
            {t('authoring.roleCodeFixed')}
          </p>
        )}

        <label className="field">
          <span className="field__label">{t('authoring.roleName')}</span>
          <input
            className="field__input"
            name="displayNameHe"
            value={displayNameHe}
            onChange={(e) => setDisplayNameHe(e.currentTarget.value)}
          />
          <span className="field__hint">{t('authoring.roleNameHint')}</span>
        </label>

        <fieldset className="field field--choice">
          <legend className="field__label">{t('authoring.capabilities')}</legend>
          {/*
            BY AREA, AND EACH AREA OPENS ON ITS OWN. **Owner, 2026-10-28:** *"רשימה מסודרת לפי
            תחומים ומודולים, עם אפשרות לפתוח כל תחום ולבחור פעולות."*

            This was 28 checkboxes in one flat run. `<details>` and not a hand-built accordion: the
            browser's own disclosure is keyboard-reachable, announced by a screen reader, and
            findable by the browser's own in-page search even while closed — which a `hidden` div
            is not, and somebody composing a role WILL search for a permission by name.

            THE COUNT IS ON THE CLOSED SUMMARY, deliberately. A closed section that says nothing
            about what is ticked inside it hides exactly the thing the reader came to check, and
            that is how a role ends up granting something nobody meant to grant.
          */}
          <div className="cap-areas">
            {capabilityAreas(catalogue).map((area) => {
              const chosen = area.codes.filter((code) => capabilities.includes(code)).length;
              return (
                <details key={area.key} className="cap-area">
                  <summary className="cap-area__head">
                    <span className="cap-area__title">{area.titleHe}</span>
                    <span className="cap-area__count">
                      {chosen === 0 ? '' : `${String(chosen)} / ${String(area.codes.length)}`}
                    </span>
                  </summary>
                  <div className="roles">
                    {area.codes.map((code) => (
                      <label key={code} className="role__choice">
                        <input
                          type="checkbox"
                          name="capabilities"
                          value={code}
                          checked={capabilities.includes(code)}
                          onChange={() => toggle(code)}
                        />
                        {/* The catalogue's own Hebrew, verbatim. A code never appears (09 §9.1 V3). */}
                        <span className="role__title">{capabilityDescriptionHe(code)}</span>
                      </label>
                    ))}
                  </div>
                </details>
              );
            })}
          </div>
          {/*
            NOTHING FOR A NEW ROLE, AND AN ⓘ WHEN EDITING ONE. *"לא צריך את כולם להפוך ל i
            רק אם זה באמת רלוונטי."* For a new role the hint said that the ticks are the role,
            which the ticks already say. For an EXISTING one it says something the screen cannot:
            un-ticking a permission removes it from everybody who holds that role, at once — a
            consequence that lands on other people and is not visible from this form.
          */}
          {editing === NEW_ROLE ? null : (
            <InfoHint aboutHe={t('authoring.replacesLabel')}>
              {t('authoring.replacesExactly')}
            </InfoHint>
          )}
        </fieldset>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={
              sending || displayNameHe === '' || (editing === NEW_ROLE && roleCode === '')
            }
          >
            {sending
              ? t('common.sending')
              : editing === NEW_ROLE
                ? t('authoring.create')
                : t('authoring.save')}
          </button>
        </div>
        {/* No standing note. The audit row is written whether or not a sentence announces it. */}
      </form>

      {outcome === null ? null : outcome.kind === 'saved' ? (
        <StateBlock
          titleHe={outcome.created ? t('authoring.createdTitle') : t('authoring.updatedTitle')}
          bodyHe={
            outcome.capabilityCount === 0
              ? t('authoring.emptyRoleBody')
              : `${t('authoring.savedGrants')}: ${String(outcome.capabilityCount)}`
          }
          nextStep={
            <a className="btn btn--nav" href={routes.tenantRoles}>
              {t('authoring.toAssignment')}
            </a>
          }
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setOutcome(null)}
          renderConflict={(failure) => {
            const reason = knownRefusal(failure.body.error);
            if (reason === null) {
              return (
                <StateBlock
                  titleHe={REFUSED_WITHOUT_DETAIL_HE.titleHe}
                  bodyHe={REFUSED_WITHOUT_DETAIL_HE.bodyHe}
                  tone="error"
                  nextStep={<p>{REFUSED_WITHOUT_DETAIL_HE.nextStepHe}</p>}
                />
              );
            }
            // The same ceiling, the same list, the same component as on the assignment screen —
            // because it is the same rule, and two renderings of it would drift.
            return <RoleRefusalNotice refusal={{ reason, excess: failure.body.excess ?? [] }} />;
          }}
        />
      )}
    </AppShell>
  );
}
